// src/native/compliance/AiConsentGate.jsx
//
// WS-7 COMPLIANCE. The one component INT mounts globally.
//
// THIS SCREEN IS TRIGGERED BY USE, NOT BY LAUNCH.
//
// It used to be a launch-time gate: open the app, get asked. That is not what
// either rule requires. App Store Guideline 5.1.2(i) requires explicit
// permission BEFORE personal data is shared with a third-party AI, and GDPR
// requires consent before PROCESSING, not before use of the app. Asking
// immediately before the first send satisfies both, and it means someone who
// only uses the tile board, scripts, history, awards or the breathing exercise
// is never asked at all, because nothing of theirs is sent anywhere.
//
// So the gate starts UNARMED and renders nothing. A guarded call site that is
// about to reach a processor calls isAiSharingAllowed(); when that refuses it
// fires AI_CONSENT_REQUIRED_EVENT, which arms this screen and records the route
// the attempt happened on. Arming is per app session and is never persisted:
// a reload means there is no attempt in flight, so nothing should be blocking.
//
// AI_CONSENT_GATE_SCOPE is 'both', so this runs on the web as well as on native.
// Four rules follow, all enforced here:
//
//   1. NEVER before a real AI attempt. Opening the app blocks nothing.
//   2. NEVER before sign-in. A signed out visitor has no AI surface to reach.
//   3. NEVER carried into the funnel. The screen shows on the route the refused
//      attempt happened on. Wander off to /pricing or tap the privacy link and
//      it steps aside; come back and it is there again. See GATE_EXEMPT_PREFIXES.
//   4. NEVER with a loading frame. Visibility is computed from a synchronous
//      localStorage read, so a user who has already accepted sees nothing at
//      all, with no flash and no layout shift.
//
// Behaviour:
//   app open                   nothing happens
//   first AI use               the screen appears on that screen, before the send
//   accepted                   one tap, gone, the original action can be retried
//   declined                   the screen can be dismissed, the app stays
//                              usable, and Timo stays quiet
//   revoked in Settings        nothing pops up there; the next AI attempt arms
//                              the gate again, so there is no dead end
//   consent version bumped     the user is asked again about the new list, at
//                              their next AI use rather than at next launch

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { useAuth } from '@/contexts/AuthContext';
import { isAiConsentRequiredHere } from './consentConfig';
import { AI_CONSENT_REQUIRED_EVENT } from './aiConsentStore';
import { useAiConsent } from './useAiConsent';
import AiConsentScreen from './AiConsentScreen';

// Routes the gate must never WANDER onto.
//
// Read this together with the armed-path rule below, because the trigger change
// inverted what this list is for. It used to stop a launch-time gate landing in
// the acquisition funnel. Now that the gate only appears after a refused AI
// call, the dangerous failure is the opposite one: if an AI call ever happens on
// a route in this list and the screen is suppressed there, the user is blocked
// with no way to consent and no way to see why.
//
// So this list no longer suppresses anything on the route the refusal happened
// on. It only stops an already armed screen FOLLOWING the user somewhere it
// would cost money or confuse:
//
//   - the whole acquisition and checkout funnel, which converts real customers
//   - the two policy pages, because the screen's own "read our privacy policy"
//     link would otherwise open a page nobody can see. Both are bundled in-app
//     React pages, so that link is never a Safari hop (Guideline 4.2).
//
// Verified against the route tree in App.jsx on 2026-08-12: no route in this
// list contains a call site that reaches Anthropic, Google, OpenAI, Deepgram or
// ElevenLabs. FbOnboarding (serving /onboarding and the whole /fb funnel),
// FbOnboardingContinue and ScratchOffer were each grepped for /api/chat,
// /api/talk, /api/tts, the framework and suggestion endpoints, playTTS, playTalk
// and every microphone hook: no hits. The onboarding voice picker plays
// pre-generated static mp3 files rather than calling /api/tts, precisely so it
// works for signed out visitors.
//
// So today the exception can never fire, and the funnel cannot be blocked no
// matter which way the armed flag falls. The exception exists so that if someone
// later adds an AI feature to one of these pages, the result is a consent screen
// the user can answer rather than a feature that silently does nothing.
//
// Matching is by prefix, so '/fb' covers the whole Facebook funnel including
// '/fb/:lang/pricing'.
const GATE_EXEMPT_PREFIXES = [
  '/login',
  '/auth',
  '/landing',
  '/landing-keiki',
  '/open-in-browser',
  '/download',
  '/privacy',
  '/terms',
  '/yc',
  '/fb',
  // The same FbOnboarding component as /fb/onboarding, reached organically
  // rather than from an ad (App.jsx). It was missing from this list, and it is
  // the one route that matters most: it CREATES THE ACCOUNT midway through, so
  // it is exactly where `user` flips from null to non-null with checkout still
  // ahead. Under the old launch-time gate that meant a full screen sheet
  // landing in front of a visitor about to pay.
  '/onboarding',
  '/web/onboarding',
  // The organic offer page, also missing before (App.jsx).
  '/scratch',
  '/start',
  '/preview',
  '/pricing',
  '/success',
  '/subscription',
  '/home/subscription',
  '/refer',
];

function isExemptPath(pathname) {
  // The landing page itself, matched exactly so it does not swallow every route.
  if (pathname === '/' || pathname === '') return true;
  return GATE_EXEMPT_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export default function AiConsentGate() {
  // Cheapest possible exit when the scope is narrowed back to native-only.
  // AiConsentGate itself calls no hooks, so hook order stays stable.
  if (!isAiConsentRequiredHere()) return null;
  return <AiConsentGateInner />;
}

function AiConsentGateInner() {
  const { t } = useLanguage();
  const { lightMode, reducedMotion } = useAccessibility();
  // useAuth is a bare useContext with no provider guard, so read defensively:
  // if this component is ever mounted outside AuthProvider it must render
  // nothing, not throw and take the whole app down with it.
  const auth = useAuth();
  const user = auth?.user || null;
  const location = useLocation();
  const { granted, minorsDone, accept, revoke } = useAiConsent();

  // Dismissal is per app session only. It is not persisted, because "I closed
  // the sheet once" is not an answer to "may we send your child's voice to five
  // companies", and the next AI attempt should ask again.
  const [dismissed, setDismissed] = useState(false);

  // THE ARMED FLAG. null means "no refused AI attempt this session", which is
  // the state the app starts and stays in for anyone who never uses an AI
  // feature. A refusal stores the route it happened on, so the screen can show
  // itself exactly there and nowhere else.
  const [armedPath, setArmedPath] = useState(null);
  const viewedRef = useRef(false);

  // Read the live path without making the listener depend on it. Re-subscribing
  // on every navigation would be wasteful, and worse, a refusal fired during the
  // teardown frame could land on a listener that is already gone.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    const arm = () => {
      const path = pathRef.current;
      // Un-dismiss as well: the user has just tried to use an AI feature again,
      // which is a new question, not the one they waved away earlier.
      setDismissed(false);
      setArmedPath(path);
      viewedRef.current = false;
      try {
        posthog.capture('ai_consent_gate_armed', { path });
      } catch {}
    };
    window.addEventListener(AI_CONSENT_REQUIRED_EVENT, arm);
    return () => window.removeEventListener(AI_CONSENT_REQUIRED_EVENT, arm);
  }, []);

  const satisfied = granted && minorsDone;

  // Disarm the moment consent is in place. Without this, a user who accepted
  // here and later switched the toggle off in Settings would have the screen
  // slam back over the Settings page from a stale arm, which is the dead end
  // this rewrite exists to remove. After a revoke the gate is unarmed, so the
  // next real AI attempt is what brings it back.
  useEffect(() => {
    if (satisfied && armedPath !== null) setArmedPath(null);
  }, [satisfied, armedPath]);

  // The rule, in full: a real refused AI attempt this session, consent still not
  // in place, not waved away since, signed in, and either on a route the gate is
  // allowed to cover or on the exact route the refusal happened on.
  const onArmedRoute = armedPath !== null && location.pathname === armedPath;
  const visible =
    armedPath !== null &&
    !satisfied &&
    !dismissed &&
    !!user &&
    (onArmedRoute || !isExemptPath(location.pathname));

  useEffect(() => {
    if (!visible || viewedRef.current) return;
    viewedRef.current = true;
    try {
      posthog.capture('ai_consent_gate_shown', {
        path: location.pathname,
        previously_declined: !granted && minorsDone,
      });
    } catch {}
  }, [visible, location.pathname, granted, minorsDone]);

  useEffect(() => {
    // Freeze the page behind the gate so a scroll gesture cannot move an app the
    // user is not allowed to use yet. Restored on unmount and on dismissal.
    if (!visible) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <AiConsentScreen
      t={t}
      lightMode={lightMode}
      reducedMotion={reducedMotion}
      // 'ai_use_gate', not the old 'first_run_gate': this is stored verbatim in
      // the consent record, and that record should say where the decision was
      // actually made. The event NAMES are untouched; only this property moved.
      onAgree={() => accept({ source: 'ai_use_gate' })}
      onDecline={() => revoke({ source: 'ai_use_gate' })}
      onUnderAge={() => {
        try {
          posthog.capture('ai_consent_under_age_declared');
        } catch {}
      }}
      onDismiss={() => {
        setDismissed(true);
        try {
          posthog.capture('ai_consent_gate_dismissed');
        } catch {}
      }}
    />
  );
}
